import { BaseQueryFn } from '@reduxjs/toolkit/query';
import { EntitlementSlugEnum } from '@electricjs/core_entity-client';

import { RootState } from '@/redux/store';

/**
 * Middleware that checks if user has required entitlements before making API requests
 */
export const entitlementMiddleware = <T extends BaseQueryFn>(
  baseQuery: T
): BaseQueryFn => {
  return async (args, api, extraOptions) => {
    // Handle both string and object queries
    const meta =
      typeof args === 'object' && 'meta' in args ? args.meta : undefined;
    const requiredEntitlements = meta?.entitlements;

    // If no entitlements are required, proceed with the request
    if (
      !requiredEntitlements ||
      requiredEntitlements.length === 0 ||
      !Array.isArray(requiredEntitlements)
    ) {
      return baseQuery(args, api, extraOptions);
    }

    // Get user's entitlements from the store
    const state = api.getState() as RootState;
    const userEntitlements =
      state.loggedUser?.organizationEntitlementSlugs || [];

    const hasAllRequiredEntitlements = requiredEntitlements.every(
      (entitlement: EntitlementSlugEnum) =>
        userEntitlements.includes(entitlement)
    );

    if (!hasAllRequiredEntitlements) {
      return {
        error: {
          status: 'ENTITLEMENT_ERROR',
          error: `Missing required entitlements: ${requiredEntitlements.join(', ')}`,
          missingEntitlements: requiredEntitlements,
          data: undefined,
        },
      };
    }

    // User has all required entitlements, proceed with the request
    return baseQuery(args, api, extraOptions);
  };
};
